/**
 * D13 — Huiswerk (practice assignments) tegen Supabase.
 *
 * Zelfde vorm als `teacherAssignmentsBackend.ts`, en bewust een eigen bestand:
 * huiswerk en toetsen zijn twee dingen. Een toets meet één onderdeel, huiswerk
 * mag over meerdere gaan — dat verschil zit in `module_ids` /
 * `target_family_ids` en het is precies de reden dat de twee panelen in de UI
 * ook gescheiden zijn gebleven. Eén gedeelde module met een vlag zou dat
 * onderscheid weer wegstoppen.
 *
 * Tabellen (0031): `practice_assignments`, `practice_assignment_targets`,
 * `practice_assignment_progress`.
 *
 * De drie harde grenzen zijn dezelfde als bij de toets-ledger, en weigeren om
 * dezelfde reden in plaats van de waarde bij te buigen:
 *   1. `classroomId: null` — kolom is `not null`; zonder klas ziet geen enkele
 *      leerkracht de opdracht via RLS.
 *   2. `status: 'completed'` — het clienttype kent die waarde, de
 *      check-constraint niet. Terugmappen zou betekenis verzinnen.
 *   3. Een id dat geen uuid is — zie `isUuid`.
 */

import { supabase } from './client';
import { error as logError } from '../../utils/logger';
import type { PracticeAssignment } from '../../assessment';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
function isUuid(id: string): boolean {
  return UUID_RE.test(id);
}
function toEpoch(value: unknown): number {
  if (typeof value !== 'string') return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}
type Row = Record<string, unknown>;
function rowToAssignment(row: Row, targets: string[], completed: string[]): PracticeAssignment {
  return {
    id: row.id as string,
    teacherId: row.created_by as string ?? '',
    classroomId: row.classroom_id as string ?? null,
    title: row.title as string ?? '',
    moduleIds: row.module_ids as PracticeAssignment['moduleIds'] ?? [],
    targetFamilyIds: row.target_family_ids as string[] ?? [],
    targetStudentIds: targets,
    wholeClass: Boolean(row.whole_class),
    questionCount: row.question_count as number ?? 0,
    createdAt: toEpoch(row.created_at),
    deadline: row.deadline ? toEpoch(row.deadline) : null,
    status: row.status as PracticeAssignment['status'],
    completedByStudentIds: completed,
    note: row.note as string ?? undefined
  };
}
export async function getPracticeAssignments(): Promise<Record<string, PracticeAssignment>> {
  const [rowsRes, targetsRes, progressRes] = await Promise.all([supabase.from('practice_assignments').select('*'), supabase.from('practice_assignment_targets').select('assignment_id, learner_id'), supabase.from('practice_assignment_progress').select('assignment_id, learner_id')]);
  if (rowsRes.error) {
    logError('[Supabase] getPracticeAssignments failed', rowsRes.error);
    return {};
  }
  // Targets en voortgang zijn aanvullend: liever het huiswerk zónder
  // voortgang tonen dan niets. Wel loggen — een lege lijst is niet te
  // onderscheiden van "niemand is klaar".
  if (targetsRes.error) {
    logError('[Supabase] getPracticeAssignments targets failed', targetsRes.error);
  }
  if (progressRes.error) {
    logError('[Supabase] getPracticeAssignments progress failed', progressRes.error);
  }
  const targetsBy = new Map<string, string[]>();
  for (const t of targetsRes.data ?? []) {
    const key = t.assignment_id as string;
    targetsBy.set(key, [...(targetsBy.get(key) ?? []), t.learner_id as string]);
  }
  const completedBy = new Map<string, string[]>();
  for (const p of progressRes.data ?? []) {
    const key = p.assignment_id as string;
    completedBy.set(key, [...(completedBy.get(key) ?? []), p.learner_id as string]);
  }
  const out: Record<string, PracticeAssignment> = {};
  for (const row of rowsRes.data ?? []) {
    const id = (row as Row).id as string;
    if (!id) continue;
    out[id] = rowToAssignment(row as Row, targetsBy.get(id) ?? [], completedBy.get(id) ?? []);
  }
  return out;
}
export async function savePracticeAssignment(id: string, assignment: PracticeAssignment): Promise<boolean> {
  if (!isUuid(id)) {
    logError(`[Supabase] savePracticeAssignment geweigerd: id "${id}" is geen uuid.`);
    return false;
  }
  if (!assignment.classroomId) {
    logError('[Supabase] savePracticeAssignment geweigerd: huiswerk zonder classroomId.');
    return false;
  }
  if (assignment.status !== 'active' && assignment.status !== 'archived') {
    logError(`[Supabase] savePracticeAssignment geweigerd: status "${assignment.status}" ` + 'wordt door het schema niet geaccepteerd (alleen active of archived).');
    return false;
  }
  const {
    error
  } = await supabase.from('practice_assignments').upsert({
    id,
    classroom_id: assignment.classroomId,
    created_by: assignment.teacherId,
    title: assignment.title,
    note: assignment.note ?? null,
    module_ids: assignment.moduleIds ?? [],
    target_family_ids: assignment.targetFamilyIds ?? [],
    whole_class: assignment.wholeClass,
    question_count: assignment.questionCount,
    deadline: assignment.deadline ? new Date(assignment.deadline).toISOString() : null,
    status: assignment.status
  });
  if (error) {
    logError('[Supabase] savePracticeAssignment failed', error);
    return false;
  }
  return syncTargets(id, assignment.targetStudentIds ?? []);
}

/**
 * Eerst toevoegen, dan verwijderen — nooit andersom. Leegmaken en opnieuw
 * vullen is korter, maar laat bij een mislukte tweede stap huiswerk achter dat
 * niemand meer kan maken.
 */
async function syncTargets(assignmentId: string, wanted: string[]): Promise<boolean> {
  const unique = Array.from(new Set(wanted));
  if (unique.length > 0) {
    const {
      error
    } = await supabase.from('practice_assignment_targets').upsert(unique.map(learnerId => ({
      assignment_id: assignmentId,
      learner_id: learnerId
    })), {
      onConflict: 'assignment_id,learner_id',
      ignoreDuplicates: true
    });
    if (error) {
      logError('[Supabase] practice syncTargets insert failed', error);
      return false;
    }
  }
  let removal = supabase.from('practice_assignment_targets').delete().eq('assignment_id', assignmentId);
  if (unique.length > 0) {
    removal = removal.not('learner_id', 'in', `(${unique.join(',')})`);
  }
  const {
    error
  } = await removal;
  if (error) {
    logError('[Supabase] practice syncTargets cleanup failed', error);
    return false;
  }
  return true;
}
export async function deletePracticeAssignment(id: string): Promise<boolean> {
  const {
    error
  } = await supabase.from('practice_assignments').delete().eq('id', id);
  if (error) {
    logError('[Supabase] deletePracticeAssignment failed', error);
    return false;
  }
  return true;
}
export async function archivePracticeAssignment(id: string): Promise<boolean> {
  // `select('id')` erbij: zonder die controle geeft een update die nul rijen
  // raakt gewoon succes terug, en dan lijkt archiveren gelukt terwijl het
  // huiswerk gewoon actief blijft staan.
  const {
    data,
    error
  } = await supabase.from('practice_assignments').update({
    status: 'archived'
  }).eq('id', id).select('id');
  if (error) {
    logError('[Supabase] archivePracticeAssignment failed', error);
    return false;
  }
  if (!data || data.length === 0) {
    logError(`[Supabase] archivePracticeAssignment: huiswerk ${id} niet gevonden of niet zichtbaar.`);
    return false;
  }
  return true;
}

/**
 * Het kind meldt zijn eigen afronding. De RLS-policy eist
 * `learner_id = auth.uid()`, en er is geen update of delete op deze tabel:
 * "af" is een logboekregel, geen veld dat teruggezet kan worden.
 *
 * Idempotent via `ignoreDuplicates` — tweemaal markeren geeft tweemaal `true`,
 * net als de localStorage-variant. Bestaat het huiswerk niet, dan blokkeert de
 * foreign key de insert en komt hier `false` uit.
 */
export async function markPracticeAssignmentCompletedByStudent(assignmentId: string, studentId: string): Promise<boolean> {
  const {
    error
  } = await supabase.from('practice_assignment_progress').upsert({
    assignment_id: assignmentId,
    learner_id: studentId,
    state: 'completed'
  }, {
    onConflict: 'assignment_id,learner_id,state',
    ignoreDuplicates: true
  });
  if (error) {
    logError('[Supabase] markPracticeAssignmentCompleted failed', error);
    return false;
  }
  return true;
}